// Datapoints fetch + batching substrate for the timeseries provider.
//
// The timeseries provider's `fetchDatapoints` seam (built in
// `datapointsFetch.ts`) resolves CDF timeseries references into the
// column-major datapoints matching `TS_REF_INNER_SCHEMA`. This module
// supplies the pieces it composes: the per-series projection
// (`seriesToColumns`), the bucket-hint → CDF fetch-spec translation
// (`bucketToFetch`), and the cross-cell request coalescer
// (`getBatchState`).
//
// Cell-level scaling. The provider's `fetch` runs once per unique series
// source; with one source per cell, a 10-col × 1000-row view result that
// calls `(unnest production)` would produce 10000 independent fetches. To
// collapse those into a single CDF round-trip (where possible), the
// datapoints fetch runs a DataLoader-style microtask batcher in a
// per-`QueryClient` shared `BatchState` (see `getBatchState`):
//
//   - Each call pushes a `{externalId, resolve, reject}` onto the
//     per-`QueryClient` pending queue and schedules a flush via
//     `queueMicrotask` (idempotent).
//   - The flush splices the current queue, dedupes externalIds, and
//     hands them to `fetchDatapointsBatched` — which chunks at
//     `CDF_DATAPOINTS_BATCH_SIZE` (100) and parallelises chunks via
//     TanStack's `fetchQuery` (so identical requests across flushes
//     share the cache entry).
//   - Per-series result rows are dispatched back to their queued
//     promises; series missing from the response (unknown id /
//     empty window) resolve to `undefined`, which the provider mints as
//     an empty inner dataset.
//
// The batch state is keyed by `QueryClient`, NOT by a factory closure:
// the provider is rebuilt inline on every render (the React Compiler does
// not memoize an impure stateful factory), so every provider built for
// one client shares one pending queue — batching is correct by
// construction, independent of render stability. The `start`/`end` window
// is likewise pinned once per `QueryClient` so every flush for a client
// produces a stable TanStack cache key. The session-level memo
// (`session.fetch` keyed per source descriptor) handles cross-flush dedup
// of identical `externalId` requests — first burst materialises,
// subsequent calls hit the cache.
//
// `:limit` per series scales with the batch's distinct-id count: wider
// batches get a smaller per-series cap so the total payload stays under
// `MAX_RAW_POINTS_PER_BATCH`.

import type { Aggregate, DatapointsQueryProperties } from '@cognite/sdk';
import { hashKey } from '@tanstack/react-query';

import { periodAlignedBoundsMs, toDateValue } from '#core/lib/expr/ops/transform/date-utils.ts';
import type { TsRefBucketHint } from '#core/lib/expr/plan.ts';
import type { Cell, Columns } from '#core/lib/expr/value.ts';
import { CDF_DATAPOINTS_BATCH_SIZE } from '#core/lib/timeseries/cdf-constants.ts';
import type { DatapointsBatchResult } from '#core/lib/timeseries/fetch-datapoints-batched.ts';

/** Default rolling window for raw-datapoints lookups (last 90 days). */
export const TS_REF_DEFAULT_WINDOW_DAYS = 90;

/**
 * Hard cap on total raw-datapoints returned per batched CDF call.
 * Scales the per-series `limit` down as the batch widens — mirrors
 * `fetchTimeseriesStep`'s `maxDatapoints / seriesPerBatch` budget.
 */
export const MAX_RAW_POINTS_PER_BATCH = 100_000;

/**
 * Compute the per-series datapoint limit for a batch of `seriesCount`
 * distinct timeseries. Caps the batch at `CDF_DATAPOINTS_BATCH_SIZE`
 * (the SDK's per-call limit); divides `MAX_RAW_POINTS_PER_BATCH` evenly
 * across the smaller of the two.
 */
export const computePerSeriesLimit = (seriesCount: number): number => {
  const effective = Math.min(Math.max(seriesCount, 1), CDF_DATAPOINTS_BATCH_SIZE);
  return Math.floor(MAX_RAW_POINTS_PER_BATCH / effective);
};

/**
 * Project a single CDF series response into the column-major `Columns`
 * shape declared by `TS_REF_INNER_SCHEMA`. `isString` lives at the
 * series level on the CDF response (per `DatapointsMetadata`); the inner
 * schema broadcasts it onto every row so downstream verbs can filter
 * without re-joining series metadata. The series-level `unit` string is
 * NOT projected: per-row unit truth rides on the value cells themselves
 * (boxed by the provider), so a parallel string column could only drift.
 *
 * Accepts `DatapointsBatchResult` (= `DatapointAggregates | Datapoints`)
 * so both raw and aggregated responses route through the same
 * projection — the SDK union shares `datapoints[].timestamp`. When
 * `aggField` is set (server-side downsampling was requested), the
 * inner `value` column is read from that aggregate field
 * (`average` / `min` / `max` / `sum` / `count`) instead of `.value`;
 * raw responses (no `aggField`) read `.value`.
 *
 * Absent (`undefined`), `null`, and empty-string values all normalise
 * to `null`. The inner `TS_REF_INNER_SCHEMA` declares `value` as
 * `numberCol`, so for a numeric series every value is coerced to a finite
 * number or `null`: a bad/uncertain datapoint can carry a non-finite value
 * (CDF serialises `NaN`/`±Infinity` as the strings `"NaN"`/`"Infinity"`),
 * which left intact violates the `numberCol` contract — degrading the
 * downstream column type to `string`/`unknown` and poisoning positional
 * `first`/`last` aggregates over the boundary datapoint. String series
 * (`isString`) pass their values through verbatim.
 */
export const seriesToColumns = (series: DatapointsBatchResult, aggField?: Aggregate): Columns => {
  const points = series.datapoints as unknown as readonly Record<string, unknown>[];
  const isString = series.isString === true;
  const readValue = (p: Record<string, unknown>): unknown => (aggField === undefined ? p.value : p[aggField]);
  return {
    timestamp: points.map((p) => p.timestamp as Cell),
    value: points.map((p) => {
      const v = readValue(p);
      if (v === undefined || v === null || v === '') {
        return null;
      }
      // Numeric series: a bad/uncertain datapoint may carry a non-finite value
      // (CDF serialises NaN/±Infinity as the strings "NaN"/"Infinity"). Coerce
      // to a finite number or null so the column upholds its `numberCol`
      // contract and never poisons positional `first`/`last` aggregates.
      if (!isString) {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      }
      return v as Cell;
    }),
    isString: points.map(() => isString as Cell),
  };
};

export type PendingRequest = {
  readonly space?: string;
  readonly externalId: string;
  /** Stable key for the derived fetch spec — flush groups by this. */
  readonly specKey: string;
  /** CDF fetch spec (window ± server-side downsampling) for this cell. */
  readonly spec: DatapointsQueryProperties;
  readonly resolve: (series: DatapointsBatchResult | undefined) => void;
  readonly reject: (error: unknown) => void;
};

const parseBound = (iso: string | undefined): number | undefined => {
  if (iso === undefined) {
    return undefined;
  }
  // Normalize through the engine's UTC kernel (the same SSOT
  // `time-bucket`/`to-date` use) so the CDF fetch window and the
  // engine's UTC bucket grid are aligned. Bare `Date.parse` is
  // timezone-inconsistent: it reads an offset-less datetime in host
  // local time but a date-only string as UTC.
  const d = toDateValue(iso);
  return d === null ? undefined : d.getTime();
};

/**
 * Translate the engine's `TsRefBucketHint` into a concrete CDF fetch
 * spec. `:window` always sets `[start, end]` — the fix for the fixed
 * 90-day window that made long `:window`s return nothing. Server-side
 * downsampling is requested ONLY when it is exact: a single `:agg`
 * symbol mapping to a CDF aggregate AND a `:period` mapping to a CDF
 * granularity — so the client-side `time-bucket` re-bucket at the same
 * period is an identity. Every other shape (multi-agg, first/last,
 * month+ periods, no hint) falls back to a raw fetch within the
 * window. `aggField` tells `seriesToColumns` which aggregate field
 * carries the value.
 */
export const bucketToFetch = (
  bucket: TsRefBucketHint | undefined,
  defaultStart: number,
  defaultEnd: number,
): { readonly spec: DatapointsQueryProperties; readonly specKey: string; readonly aggField?: Aggregate } => {
  const w0 = bucket?.window?.[0];
  const w1 = bucket?.window?.[1];
  const period = bucket?.period;
  // Align the CDF fetch range to the SAME UTC period grid the engine
  // buckets on (core `periodGrid`/`bucketTimestamp` via the shared
  // `periodAlignedBoundsMs` SSOT), so the first and last grid buckets
  // have complete underlying data. Without this the engine's UTC
  // boundary bucket is fed a partial period and renders empty /
  // shifted ("off by one day") for an offset-bearing `:window`.
  const aligned =
    w0 !== undefined && w1 !== undefined && period !== undefined ? periodAlignedBoundsMs(w0, w1, period) : null;
  const start = aligned?.start ?? parseBound(w0) ?? defaultStart;
  const end = aligned?.end ?? parseBound(w1) ?? defaultEnd;
  // Server-side downsampling is intentionally disabled: CDF's aggregate
  // datapoints endpoint caps "sum of limits" at 10_000 per call (vs.
  // 100_000 for raw points), which trips on multi-series batches the
  // engine routinely produces. The hint's `aggs`/`period` still drives
  // the fetch WINDOW (above) so the right time range is requested; the
  // engine's `time-bucket` then reduces the raw points client-side at
  // the same `period`. Re-enable by restoring the commented branch
  // below and reworking `computePerSeriesLimit` to respect the lower
  // aggregate-points budget per call.
  // const aggSym = bucket?.aggs?.length === 1 ? bucket.aggs[0] : undefined;
  // const cdfAgg = aggSym === undefined ? undefined : AGG_TO_CDF[aggSym];
  // const granularity = bucket?.period === undefined ? undefined : PERIOD_TO_GRANULARITY[bucket.period];
  // if (cdfAgg !== undefined && granularity !== undefined) {
  //   const spec: DatapointsQueryProperties = { start, end, granularity, aggregates: [cdfAgg] };
  //   return { spec, specKey: JSON.stringify(spec), aggField: cdfAgg };
  // }
  const spec: DatapointsQueryProperties = { start, end };
  // `hashKey` is TanStack Query's deterministic serializer (object-key-
  // order independent) — same SSOT the codebase uses for tool-identity
  // hashing. Avoids the `JSON.stringify` order-sensitivity that would
  // split otherwise-identical specs into separate fetch groups.
  return { spec, specKey: hashKey([spec]) };
};
/**
 * Per-{@link QueryClient} shared microtask batch state.
 *
 * The coalescer's pending queue, scheduled-flush flag, and the
 * construct-time-pinned default window MUST outlive any single React
 * render. The dataset-provider registry (and thus the timeseries
 * provider) is rebuilt inline in `ChatSessionProvider`'s JSX
 * `value={{…}}` and is therefore re-created on every render — the React
 * Compiler does not (and need not) memoize an impure stateful factory.
 * Keying the state by the `QueryClient` makes request batching correct
 * *by construction*, independent of render stability: every provider
 * built for the same client shares one pending queue and one pinned
 * window.
 *
 * `QueryClient` is the natural batch scope — `fetchDatapointsBatched`
 * already memoizes through it, and it is a stable per-thread/app
 * singleton. The `client` captured here is the one seen on first
 * construction for that `QueryClient`; in this app both come from
 * app-level providers and never vary independently. A `WeakMap` lets
 * the state be GC'd with its `QueryClient` (thread / app teardown) —
 * no leak.
 */
